/* eslint-disable max-lines */
/**
 * Settings — unified workspace and repository settings.
 *
 * NOTE: DashboardLayout provides OrgProvider, so all org/project
 * hooks must be called inside SettingsInner (rendered as a child
 * of DashboardLayout).
 */
import * as React from 'react';
import {
  GitBranch,
  Plus,
  CircleNotch,
  CheckCircle,
  Warning,
  MagnifyingGlass,
  ArrowLeft,
  ArrowRight,
  Trash,
  Copy,
  Check,
  Key,
  Eye,
  EyeSlash,
  ArrowClockwise,
  Users,
  UserPlus,
  GearSix,
  CreditCard,
  Bell,
  PlugsConnected,
  ChartBar,
  Palette,
  Scales,
  ListChecks,
} from '@phosphor-icons/react';
import { Badge, Switch, useTheme, useToast } from '@fragments-sdk/ui';
import { useQuery, useMutation, useAction } from 'convex/react';
import { useUser } from '@clerk/tanstack-react-start';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { api } from '@/convex';
import type { Id } from '@/convex';
import {
  ContentState,
  DashboardLayout,
  PageHeader,
  SidebarButton,
} from '@/components/layout';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useProjectContext } from '@/hooks/useProjectContext';
import { useGitHubConnect } from '@/components/github/useGitHubConnect';
import { InviteDialog } from '@/components/layout/DashboardDialogs';
import { useGitHubRepos } from '@/hooks/useGitHubRepos';
import { useOrgApiKey } from '@/hooks/useOrgApiKey';
import { useRepoScan } from '@/hooks/useRepoScan';
import type { ScanResult } from '@/lib/github-types';
import {
  BillingSection,
  UsageSection,
  BillingAuditLog,
} from '@/components/billing';
import { deriveTokenConfig } from '@/lib/token-config-derivation';
import type { CodebaseProfile } from '@/lib/github-types';
import {
  FEATURE_DETAILS,
  PLANS,
  cheapestPlanWithFeature,
  getQuota,
  hasCloudPolicyManagement,
  hasFeature,
  type Feature,
  type PlanId,
} from '../../../../convex/billing/plans';
import type { Icon } from '@phosphor-icons/react';
import { CanonicalSourcesPanel } from './canonical-sources-panel';
import { CanonicalReviewPanel } from './canonical-review-panel';
import { AccountDeletionPanel } from './account-deletion-panel';
import s from './settings.module.scss';

// ── Types ────────────────────────────────────────────────────────

type ConnectPhase = 'idle' | 'picking' | 'scanning' | 'done';

interface ScanArgs {
  orgId: Id<'orgs'>;
  projectId: Id<'projects'>;
  repoFullName: string;
}

// ── Tab configs ──────────────────────────────────────────────────

interface TabDef {
  key: string;
  label: string;
  icon: Icon;
}

interface TabGroup {
  label: string;
  meta?: string;
  tabs: TabDef[];
}

const WORKSPACE_TABS: TabDef[] = [
  { key: 'general', label: 'General', icon: GearSix },
  { key: 'usage', label: 'Usage', icon: ChartBar },
  { key: 'billing', label: 'Billing', icon: CreditCard },
  { key: 'team', label: 'Team', icon: Users },
  { key: 'notifications', label: 'Notifications', icon: Bell },
  { key: 'projects', label: 'Repositories', icon: GitBranch },
];

const REPOSITORY_TABS: TabDef[] = [
  { key: 'sources', label: 'Repository', icon: PlugsConnected },
  { key: 'tokens', label: 'Token sources', icon: Palette },
  { key: 'governance', label: 'Governance policy', icon: Scales },
  { key: 'mappings', label: 'Canonical mappings', icon: ListChecks },
  { key: 'danger', label: 'Danger zone', icon: Trash },
];

const SETTINGS_TABS = [...WORKSPACE_TABS, ...REPOSITORY_TABS];

function getSettingsTab(value: unknown) {
  return SETTINGS_TABS.some((tab) => tab.key === value)
    ? (value as string)
    : null;
}

// ── Outer wrapper (provides OrgProvider via DashboardLayout) ────

export function Settings() {
  return (
    <DashboardLayout activeNav="Settings" flush>
      <SettingsInner />
    </DashboardLayout>
  );
}

// ── Inner component (has access to OrgProvider context) ─────────

function SettingsInner() {
  const { orgId, org, activeRole, isLoading: orgLoading } = useOrgContext();
  const { selectedProject, isLoading: projectLoading } = useProjectContext();

  if (orgLoading || projectLoading) {
    return (
      <div className={s.settingsPage}>
        <div className={s.detailPanel}>
          <ContentState
            className={s.loadingState}
            icon={<CircleNotch size={18} className={s.spinIcon} />}
            title="Loading settings..."
            minHeight={320}
          />
        </div>
      </div>
    );
  }

  return (
    <SettingsView
      orgId={orgId}
      orgName={org?.name ?? 'Organization'}
      orgPlan={(org?.plan ?? 'free') as PlanId}
      isDemoOrg={Boolean((org as { isDemo?: boolean } | undefined)?.isDemo)}
      activeRole={activeRole}
      selectedProject={selectedProject}
    />
  );
}

// ── Sidebar Nav ─────────────────────────────────────────────────

function SettingsNav({
  title,
  subtitle,
  groups,
  activeTab,
  onTabChange,
}: {
  title: string;
  subtitle: string;
  groups: TabGroup[];
  activeTab: string;
  onTabChange: (key: string) => void;
}) {
  return (
    <div className={s.navPanel}>
      <div className={s.navHeader}>
        <h2 className={s.navTitle}>{title}</h2>
        <p className={s.navSubtitle}>{subtitle}</p>
      </div>
      <div className={s.navList}>
        {groups.map((group) => (
          <div key={group.label} className={s.navGroup}>
            <div className={s.navGroupHeader}>
              <span className={s.navGroupLabel}>{group.label}</span>
              {group.meta ? (
                <span className={s.navGroupMeta}>{group.meta}</span>
              ) : null}
            </div>
            {group.tabs.map((tab) => {
              const IconComp = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <SidebarButton
                  key={tab.key}
                  active={isActive}
                  leading={
                    <IconComp
                      size={14}
                      weight={isActive ? 'fill' : 'regular'}
                    />
                  }
                  onClick={() => onTabChange(tab.key)}
                >
                  {tab.label}
                </SidebarButton>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Notification Preferences Toggle ─────────────────────────────

function NotificationPreferences({ orgId }: { orgId: Id<'orgs'> }) {
  const prefs = useQuery(api.notificationPreferences.getForUser, { orgId });
  const updatePrefs = useMutation(api.notificationPreferences.update);
  const isEnabled = prefs === null || prefs?.weeklyDigest !== false;
  const [saving, setSaving] = React.useState(false);

  async function handleToggle() {
    setSaving(true);
    try {
      await updatePrefs({ orgId, weeklyDigest: !isEnabled });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={s.card}>
      <div className={s.cardRow}>
        <div className={s.cardRowInfo}>
          <p className={s.cardRowLabel}>Weekly findings digest</p>
          <p className={s.cardRowDesc}>
            Receive a summary of new and resolved findings every Monday.
          </p>
        </div>
        <button
          type="button"
          className={s.toggle}
          role="switch"
          aria-checked={isEnabled}
          data-checked={isEnabled || undefined}
          onClick={handleToggle}
          disabled={saving || prefs === undefined}
        >
          <span className={s.toggleThumb} />
        </button>
      </div>
    </div>
  );
}

// ── Theme Preference ────────────────────────────────────────────

function ThemePreference() {
  const { mode, resolvedMode, setMode } = useTheme();
  const isDark = mode === 'system' ? resolvedMode === 'dark' : mode === 'dark';

  return (
    <div className={s.cardRow}>
      <div className={s.cardRowInfo}>
        <p className={s.cardRowLabel}>Theme</p>
        <p className={s.cardRowDesc}>Switch between light and dark mode.</p>
      </div>
      <div className={s.themeSwitchControl}>
        <span className={s.themeSwitchLabel}>{isDark ? 'Dark' : 'Light'}</span>
        <Switch
          checked={isDark}
          onCheckedChange={(checked) => setMode(checked ? 'dark' : 'light')}
          aria-label="Dark mode"
          size="sm"
        />
      </div>
    </div>
  );
}

// ── Team Members ───────────────────────────────────────────────

function TeamMembers({
  orgId,
  isAdmin,
}: {
  orgId: Id<'orgs'>;
  isAdmin: boolean;
}) {
  const members = useQuery(api.memberships.listByOrg, { orgId });
  const invites = useQuery(api.invites.listByOrg, { orgId });
  const revokeMut = useMutation(api.invites.revoke);
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [revokingId, setRevokingId] = React.useState<string | null>(null);

  const pendingInvites = invites ?? [];
  const totalCount = (members?.length ?? 0) + pendingInvites.length;

  const roleSummary = React.useMemo(() => {
    if (!members) return '';
    const counts: Record<string, number> = {};
    for (const m of members) {
      counts[m.role] = (counts[m.role] ?? 0) + 1;
    }
    return ['owner', 'admin', 'member']
      .filter((r) => counts[r])
      .map((r) => `${counts[r]} ${r}${(counts[r] ?? 0) > 1 ? 's' : ''}`)
      .join(' \u00b7 ');
  }, [members]);

  async function handleRevoke(inviteId: string) {
    setRevokingId(inviteId);
    try {
      await revokeMut({ inviteId: inviteId as Id<'invites'> });
    } finally {
      setRevokingId(null);
    }
  }

  return (
    <>
      {members === undefined ? (
        <div className={s.loadingState}>
          <CircleNotch size={14} className={s.spinIcon} />
          Loading team…
        </div>
      ) : (
        <div className={s.section}>
          <div className={s.sectionHeader}>
            <p className={s.sectionTitle}>Members ({totalCount})</p>
            {isAdmin && (
              <button
                type="button"
                className={s.sectionAction}
                onClick={() => setInviteOpen(true)}
              >
                <UserPlus size={12} />
                Invite
              </button>
            )}
          </div>
          <div className={s.card}>
            {roleSummary && (
              <div className={s.cardRow}>
                <div className={s.cardRowInfo}>
                  <p className={s.cardRowLabel}>Roles</p>
                  <p className={s.cardRowDesc}>{roleSummary}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {pendingInvites.length > 0 && (
        <div className={s.section}>
          <p className={s.sectionTitle}>Pending invites</p>
          <div className={s.card}>
            {pendingInvites.map((inv) => (
              <div key={inv._id} className={s.cardRow}>
                <div className={s.cardRowInfo}>
                  <p className={s.cardRowLabel}>{inv.email}</p>
                  <p className={s.cardRowDesc}>Invited as {inv.role}</p>
                </div>
                {isAdmin && (
                  <button
                    type="button"
                    className={s.sectionAction}
                    onClick={() => handleRevoke(inv._id)}
                    disabled={revokingId === inv._id}
                    title="Revoke invite"
                  >
                    <Trash size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {inviteOpen && <InviteDialog onClose={() => setInviteOpen(false)} />}
    </>
  );
}

// ── Org Tab Panels ──────────────────────────────────────────────

function GeneralPanel({
  orgId,
  orgName,
  isAdmin,
  isDemoOrg,
  plan,
}: {
  orgId: Id<'orgs'> | undefined;
  orgName: string;
  isAdmin: boolean;
  isDemoOrg: boolean;
  plan: PlanId;
}) {
  const { success, error } = useToast();
  const hasMachineApi = hasFeature(plan, 'apiAccess');

  // ── API key state ──
  const { apiKey, rotating, rotate } = useOrgApiKey({
    orgId,
    enabled: isAdmin && hasMachineApi,
  });
  const [copied, setCopied] = React.useState(false);
  const [showKey, setShowKey] = React.useState(false);
  const [confirmRotate, setConfirmRotate] = React.useState(false);

  async function copyKey() {
    if (!apiKey) return;
    const writeText = navigator.clipboard?.writeText;
    if (!writeText) {
      error('Copy failed', 'Clipboard unavailable');
      return;
    }
    try {
      await writeText.call(navigator.clipboard, apiKey);
      setCopied(true);
      success('API key copied');
      setTimeout(() => setCopied(false), 1600);
    } catch (err) {
      error(
        'Copy failed',
        err instanceof Error ? err.message : 'Clipboard unavailable',
      );
    }
  }

  async function handleRotate() {
    const nextKey = await rotate();
    if (nextKey) {
      setConfirmRotate(false);
      setShowKey(true);
    }
  }

  return (
    <div className={s.detailContent}>
      <PageHeader
        title={orgName}
        titleAs="h2"
        description="General settings for your organization."
        size="md"
      />

      <div className={s.section}>
        <p className={s.sectionTitle}>Workspace</p>
        <div className={s.card}>
          <div className={s.cardRow}>
            <div className={s.cardRowInfo}>
              <p className={s.cardRowLabel}>{orgName}</p>
              <p className={s.cardRowDesc}>
                {isDemoOrg
                  ? 'This public demo workspace is read-only. Create your own workspace to manage keys, billing, members, repositories, and governance policy.'
                  : isAdmin
                    ? 'You can manage workspace-level automation, billing, members, and destructive actions.'
                    : 'You have read-only access to workspace settings. Ask an owner or admin to manage keys, billing, members, and destructive actions.'}
              </p>
            </div>
            <Badge variant={isDemoOrg ? 'outline' : 'success'} size="sm">
              {isDemoOrg ? 'Demo' : PLANS[plan].label}
            </Badge>
          </div>
          <ThemePreference />
        </div>
      </div>

      {/* ── API Key ── */}
      {isAdmin && (
        <div className={s.section}>
          <div className={s.sectionHeader}>
            <p className={s.sectionTitle}>API key</p>
            <FeatureAvailabilityBadge plan={plan} feature="apiAccess" />
          </div>
          <p className={s.sectionDesc}>
            Used by the CLI and CI workflows to report governance findings. Set
            as <code>FRAGMENTS_API_KEY</code> in your repo secrets.
          </p>
          {!hasMachineApi ? (
            <PlanLockedCard plan={plan} feature="apiAccess" />
          ) : (
            <div className={s.card}>
              <div className={s.cardRow}>
                <div
                  className={s.cardRowInfo}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <Key size={14} className={s.apiKeyIcon} />
                  {apiKey ? (
                    <code className={s.apiKeyValue}>
                      {showKey
                        ? apiKey
                        : `${apiKey.slice(0, 11)}${'•'.repeat(8)}`}
                    </code>
                  ) : (
                    <span className={s.apiKeyLoading}>Loading…</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    type="button"
                    className={s.apiKeyActionBtn}
                    onClick={() => setShowKey(!showKey)}
                    disabled={!apiKey}
                    title={showKey ? 'Hide key' : 'Reveal key'}
                  >
                    {showKey ? <EyeSlash size={14} /> : <Eye size={14} />}
                  </button>
                  <button
                    type="button"
                    className={s.apiKeyActionBtn}
                    onClick={copyKey}
                    disabled={!apiKey}
                    title="Copy to clipboard"
                    data-copied={copied || undefined}
                  >
                    {copied ? (
                      <Check size={14} weight="bold" />
                    ) : (
                      <Copy size={14} />
                    )}
                  </button>
                </div>
              </div>
              {!confirmRotate ? (
                <div className={s.cardRow}>
                  <button
                    type="button"
                    className={s.sectionAction}
                    onClick={() => setConfirmRotate(true)}
                  >
                    <ArrowClockwise size={13} />
                    Regenerate key
                  </button>
                </div>
              ) : (
                <div
                  className={s.cardRow}
                  style={{
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: 10,
                  }}
                >
                  <div className={s.apiKeyConfirmMsg}>
                    <Warning size={14} />
                    <span>
                      This will invalidate the current key. Update your CI
                      secrets after regenerating.
                    </span>
                  </div>
                  <div className={s.apiKeyConfirmActions}>
                    <button
                      type="button"
                      className={s.disconnectConfirmBtn}
                      data-variant="danger"
                      onClick={handleRotate}
                      disabled={rotating}
                    >
                      {rotating ? 'Regenerating…' : 'Regenerate'}
                    </button>
                    <button
                      type="button"
                      className={s.disconnectConfirmBtn}
                      onClick={() => setConfirmRotate(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Danger zone ── */}
      <AccountDeletionPanel isDemoOrg={isDemoOrg} />
    </div>
  );
}

function ProjectsPanel({ isAdmin }: { isAdmin: boolean }) {
  const { projects, setProject } = useProjectContext();
  function handleCreateProject() {
    window.dispatchEvent(new CustomEvent('fragments:create-project'));
  }

  return (
    <div className={s.detailContent}>
      <PageHeader
        title="Repositories"
        titleAs="h2"
        description="Manage your organization's connected repositories."
        size="md"
      />

      <div className={s.section}>
        <div className={s.sectionHeader}>
          <p className={s.sectionTitle}>All repositories ({projects.length})</p>
          {isAdmin && (
            <button
              type="button"
              className={s.sectionAction}
              onClick={handleCreateProject}
            >
              <Plus size={12} />
              Add repository
            </button>
          )}
        </div>

        {projects.length === 0 ? (
          <div className={s.card}>
            <div className={s.cardRow}>
              <div className={s.cardRowInfo}>
                <p className={s.cardRowLabel}>No repositories yet</p>
                <p className={s.cardRowDesc}>
                  Connect a repository to start tracking your design system.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className={s.card}>
            {projects.map((p) => (
              <button
                key={p._id}
                type="button"
                className={s.cardRowBtn}
                onClick={() => setProject(p.slug)}
              >
                <GitBranch
                  size={15}
                  weight="fill"
                  className={s.cardRowBtnIcon}
                />
                <div className={s.cardRowInfo}>
                  <p className={s.cardRowLabel}>{p.name}</p>
                  {p.repoFullName && (
                    <p className={s.cardRowDesc}>
                      <GitBranch size={11} />
                      {p.repoFullName}
                    </p>
                  )}
                </div>
                <ArrowRight size={13} className={s.cardRowArrow} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FeatureAvailabilityBadge({
  plan,
  feature,
}: {
  plan: PlanId;
  feature: Feature;
}) {
  const enabled = hasFeature(plan, feature);
  const requiredPlan = cheapestPlanWithFeature(feature);
  return (
    <Badge variant={enabled ? 'success' : 'outline'} size="sm">
      {enabled
        ? `${PLANS[plan].label} includes this`
        : requiredPlan
          ? `Requires ${PLANS[requiredPlan].label}`
          : 'Unavailable'}
    </Badge>
  );
}

function PlanLockedCard({ plan, feature }: { plan: PlanId; feature: Feature }) {
  const detail = FEATURE_DETAILS[feature];
  const requiredPlan = cheapestPlanWithFeature(feature);
  const requiredLabel = requiredPlan
    ? PLANS[requiredPlan].label
    : 'a paid plan';
  return (
    <div className={s.card}>
      <div className={s.cardRow}>
        <div className={s.cardRowInfo}>
          <p className={s.cardRowLabel}>{detail.label} is not active</p>
          <p className={s.cardRowDesc}>
            Your workspace is on {PLANS[plan].label}. {detail.description}{' '}
            Upgrade to {requiredLabel} to enable this workflow.
          </p>
        </div>
        <Badge variant="outline" size="sm">
          {requiredLabel}
        </Badge>
      </div>
    </div>
  );
}

function DemoReadOnlyPanel({
  title,
  description,
  label,
  body,
}: {
  title: string;
  description: string;
  label: string;
  body: string;
}) {
  return (
    <div className={s.detailContent}>
      <PageHeader
        title={title}
        titleAs="h2"
        description={description}
        size="md"
      />
      <div className={s.card}>
        <div className={s.cardRow}>
          <div className={s.cardRowInfo}>
            <p className={s.cardRowLabel}>{label}</p>
            <p className={s.cardRowDesc}>{body}</p>
          </div>
          <Badge variant="outline" size="sm">
            Demo
          </Badge>
        </div>
      </div>
    </div>
  );
}

function DemoDangerPanel({
  selectedProject,
}: {
  selectedProject:
    | {
        name: string;
        repoFullName?: string;
      }
    | undefined;
}) {
  const repositoryName =
    selectedProject?.repoFullName ?? selectedProject?.name ?? 'Demo repository';

  return (
    <div className={s.detailContent}>
      <PageHeader
        title="Danger zone"
        titleAs="h2"
        description={`Irreversible actions for ${repositoryName}.`}
        size="md"
      />
      <div className={s.section} data-danger>
        <p className={s.sectionTitle}>Danger zone</p>
        <div className={s.dangerRow}>
          <div className={s.dangerInfo}>
            <p className={s.dangerLabel}>Delete repository</p>
            <p className={s.dangerDesc}>
              Public demo repositories are read-only. Connect your own
              repository to manage destructive actions.
            </p>
          </div>
          <button type="button" className={s.dangerBtn} disabled>
            Delete repository
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Unified settings view ───────────────────────────────────────

function SettingsView({
  orgId,
  orgName,
  orgPlan,
  isDemoOrg,
  activeRole,
  selectedProject,
}: {
  orgId: Id<'orgs'> | undefined;
  orgName: string;
  orgPlan: PlanId;
  isDemoOrg: boolean;
  activeRole: string | null | undefined;
  selectedProject:
    | {
        name: string;
        repoFullName?: string;
      }
    | undefined;
}) {
  const isAdmin = activeRole === 'owner' || activeRole === 'admin';
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { tab?: string };
  const urlTab = getSettingsTab(search.tab) ?? 'general';
  const [activeTab, setActiveTab] = React.useState(urlTab);
  const repositoryMeta =
    selectedProject?.repoFullName ?? selectedProject?.name ?? 'No repository';
  const groups = React.useMemo<TabGroup[]>(
    () => [
      { label: 'Workspace', tabs: WORKSPACE_TABS },
      {
        label: 'Repository setup',
        meta: repositoryMeta,
        tabs: REPOSITORY_TABS,
      },
    ],
    [repositoryMeta],
  );

  React.useEffect(() => {
    setActiveTab(urlTab);
  }, [urlTab]);

  const handleTabChange = React.useCallback(
    (tab: string) => {
      setActiveTab(tab);
      navigate({
        search: (prev: Record<string, unknown>) => {
          const next = { ...prev };
          if (tab === 'general') {
            delete next.tab;
          } else {
            next.tab = tab;
          }
          return next;
        },
      } as Parameters<typeof navigate>[0]);
    },
    [navigate],
  );

  return (
    <div className={s.settingsPage}>
      <SettingsNav
        title="Settings"
        subtitle={orgName}
        groups={groups}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />
      <div className={s.detailPanel}>
        {activeTab === 'general' && (
          <GeneralPanel
            orgId={orgId}
            orgName={orgName}
            isAdmin={isAdmin}
            isDemoOrg={isDemoOrg}
            plan={orgPlan}
          />
        )}
        {activeTab === 'usage' && orgId && (
          <div className={s.detailContent}>
            <PageHeader
              title="Usage"
              titleAs="h2"
              description="Track AI suggestions, enrichments, and resource consumption."
              size="md"
            />
            <UsageSection plan={orgPlan} />
          </div>
        )}
        {activeTab === 'billing' && orgId && (
          <div className={s.detailContent}>
            <PageHeader
              title="Billing"
              titleAs="h2"
              description="Manage your subscription and billing details."
              size="md"
            />
            <BillingSection plan={orgPlan} isAdmin={isAdmin} />
            {isAdmin ? (
              <BillingAuditLog orgId={orgId} />
            ) : (
              <div className={s.card}>
                <div className={s.cardRow}>
                  <div className={s.cardRowInfo}>
                    <p className={s.cardRowLabel}>Billing management</p>
                    <p className={s.cardRowDesc}>
                      Ask an owner or admin to manage subscription settings and
                      review billing events.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {activeTab === 'team' && orgId && (
          <>
            {isDemoOrg ? (
              <DemoReadOnlyPanel
                title="Team"
                description="Manage team members and invitations."
                label="Demo workspace"
                body="Team membership is read-only in the public demo. Create a workspace to invite teammates and manage roles."
              />
            ) : (
              <div className={s.detailContent}>
                <PageHeader
                  title="Team"
                  titleAs="h2"
                  description="Manage team members and invitations."
                  size="md"
                />
                <TeamMembers orgId={orgId} isAdmin={isAdmin} />
              </div>
            )}
          </>
        )}
        {activeTab === 'notifications' && orgId && (
          <>
            {isDemoOrg ? (
              <DemoReadOnlyPanel
                title="Notifications"
                description="Configure how you receive updates from Fragments."
                label="Demo preferences"
                body="Notification preferences are saved per user, so they are disabled in the public demo."
              />
            ) : (
              <div className={s.detailContent}>
                <PageHeader
                  title="Notifications"
                  titleAs="h2"
                  description="Configure how you receive updates from Fragments."
                  size="md"
                />
                <NotificationPreferences orgId={orgId} />
              </div>
            )}
          </>
        )}
        {activeTab === 'projects' && <ProjectsPanel isAdmin={isAdmin} />}
        {activeTab === 'sources' &&
          (isDemoOrg ? (
            <DemoReadOnlyPanel
              title={selectedProject?.repoFullName ?? 'Repository'}
              description="Manage this repository connection and its settings."
              label="Demo repository"
              body="Repository connection settings are read-only in the public demo. Connect your own repo to install GitHub, scan files, and manage bindings."
            />
          ) : (
            <SourcesPanel />
          ))}
        {activeTab === 'tokens' &&
          (isDemoOrg ? (
            <DemoReadOnlyPanel
              title="Token sources"
              description="Review token detection for this repository."
              label="Demo token configuration"
              body="Token source editing is read-only in the public demo. Connect your own repo to edit prefixes and reset detection from scans."
            />
          ) : (
            <TokensPanel />
          ))}
        {activeTab === 'governance' && <GovernancePanel plan={orgPlan} />}
        {activeTab === 'mappings' && (
          <CanonicalReviewPanel
            orgId={orgId}
            isAdmin={isAdmin}
            isDemoOrg={isDemoOrg}
          />
        )}
        {activeTab === 'danger' &&
          (isDemoOrg ? (
            <DemoDangerPanel selectedProject={selectedProject} />
          ) : (
            <DangerPanel />
          ))}
      </div>
    </div>
  );
}

// ── Governance Panel (repository-level) ─────────────────────────

function GovernancePanel({ plan }: { plan: PlanId }) {
  const { selectedProjectId } = useProjectContext();

  if (!selectedProjectId) {
    return (
      <div className={s.detailContent}>
        <ContentState
          className={s.emptyState}
          title="Select a repository to configure governance."
          minHeight={160}
        />
      </div>
    );
  }

  if (!hasCloudPolicyManagement(plan)) {
    return (
      <div className={s.detailContent}>
        <PageHeader
          title="Governance policy"
          titleAs="h2"
          description="Cloud-managed governance policy is available on paid plans."
          size="md"
        />
        <PlanLockedCard plan={plan} feature="policyManagement" />
      </div>
    );
  }

  return <CanonicalSourcesPanel projectId={selectedProjectId} />;
}

// ── Tokens Panel (repository-level) ─────────────────────────────

function TokensPanel() {
  const { user } = useUser();
  const { selectedProjectId, selectedProject } = useProjectContext();
  const tokenConfig = useQuery(
    api.projects.getTokenConfig,
    selectedProjectId ? { projectId: selectedProjectId } : 'skip',
  );
  const codebaseProfile = useQuery(
    api.projects.getCodebaseProfile,
    selectedProjectId ? { projectId: selectedProjectId } : 'skip',
  );
  const updateTokenConfig = useMutation(api.projects.updateTokenConfig);

  const [prefixDraft, setPrefixDraft] = React.useState<string>('');
  const [prefixEditing, setPrefixEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [redetecting, setRedetecting] = React.useState(false);

  React.useEffect(() => {
    if (tokenConfig && !prefixEditing) {
      setPrefixDraft(tokenConfig.prefix ?? '');
    }
  }, [tokenConfig, prefixEditing]);

  if (!selectedProjectId || !selectedProject) {
    return (
      <div className={s.detailContent}>
        <PageHeader
          title="Token sources"
          titleAs="h2"
          description="Select a repository to review token configuration."
          size="md"
        />
        <ContentState
          className={s.emptyState}
          title="No repository selected."
          minHeight={160}
        />
      </div>
    );
  }

  if (tokenConfig === undefined || codebaseProfile === undefined) {
    return (
      <div className={s.detailContent}>
        <ContentState
          className={s.loadingState}
          icon={<CircleNotch size={18} className={s.spinIcon} />}
          title="Loading token configuration..."
          minHeight={240}
        />
      </div>
    );
  }

  const confidence = codebaseProfile
    ? deriveTokenConfig(codebaseProfile as unknown as CodebaseProfile)
        .confidence
    : { level: 'high' as const, reasons: [] };
  const tokenCount = codebaseProfile?.tokens.totalCount ?? 0;
  const format = codebaseProfile?.tokens.format ?? 'none';
  const sources = tokenConfig?.sources ?? [];

  async function savePrefix() {
    if (!selectedProjectId || !tokenConfig || !user) return;
    setSaving(true);
    try {
      await updateTokenConfig({
        projectId: selectedProjectId,
        clerkUserId: user.id,
        tokenConfig: {
          ...tokenConfig,
          prefix: prefixDraft.trim() || undefined,
        },
      });
      setPrefixEditing(false);
    } finally {
      setSaving(false);
    }
  }

  async function redetectFromScan() {
    if (!selectedProjectId || !codebaseProfile || !user) return;
    setRedetecting(true);
    try {
      const { deriveTokenConfig } =
        await import('@/lib/token-config-derivation');
      const { tokenConfig: fresh } = deriveTokenConfig(
        codebaseProfile as unknown as CodebaseProfile,
      );
      await updateTokenConfig({
        projectId: selectedProjectId,
        clerkUserId: user.id,
        tokenConfig: fresh,
      });
    } finally {
      setRedetecting(false);
    }
  }

  return (
    <div className={s.detailContent}>
      <PageHeader
        title="Token sources"
        titleAs="h2"
        description={
          tokenCount > 0
            ? `We detected ${tokenCount} design token${tokenCount === 1 ? '' : 's'} in ${selectedProject?.name ?? 'this repository'}.`
            : `No formal token system was detected in ${selectedProject?.name ?? 'this repository'}.`
        }
        size="md"
      />

      {confidence.level === 'low' && confidence.reasons.length > 0 && (
        <div className={s.reviewBanner}>
          <Warning size={14} weight="fill" className={s.reviewBannerIcon} />
          <div className={s.reviewBannerBody}>
            <p className={s.reviewBannerTitle}>Review detected tokens</p>
            <ul className={s.reviewBannerReasons}>
              {confidence.reasons.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className={s.card}>
        <div className={s.cardRow}>
          <div className={s.cardRowInfo}>
            <p className={s.cardRowLabel}>Format</p>
            <p className={s.cardRowDesc}>{formatTokenFormat(format)}</p>
          </div>
        </div>
        <div className={s.cardRow}>
          <div className={s.cardRowInfo}>
            <p className={s.cardRowLabel}>Prefix</p>
            {prefixEditing ? (
              <input
                className={s.prefixInput}
                value={prefixDraft}
                onChange={(e) => setPrefixDraft(e.target.value)}
                placeholder="--acme-"
                autoFocus
              />
            ) : (
              <p className={s.cardRowDesc}>
                {tokenConfig?.prefix ?? (
                  <span className={s.muted}>— not set —</span>
                )}
              </p>
            )}
          </div>
          {prefixEditing ? (
            <div className={s.inlineActions}>
              <button
                type="button"
                className={s.inlineBtn}
                onClick={() => {
                  setPrefixEditing(false);
                  setPrefixDraft(tokenConfig?.prefix ?? '');
                }}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className={s.inlineBtnPrimary}
                onClick={savePrefix}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className={s.inlineBtn}
              onClick={() => setPrefixEditing(true)}
              disabled={!tokenConfig}
            >
              Edit
            </button>
          )}
        </div>
        <div className={s.cardRow}>
          <div className={s.cardRowInfo}>
            <p className={s.cardRowLabel}>Styling approach</p>
            <p className={s.cardRowDesc}>
              {formatStylingApproach(tokenConfig?.stylingApproach)}
            </p>
          </div>
        </div>
      </div>

      <div className={s.section}>
        <p className={s.sectionTitle}>Sources ({sources.length})</p>
        {sources.length === 0 ? (
          <p className={s.sectionDesc}>
            No token source files were detected. Fragments will still flag
            hardcoded values in your components — the values that repeat most
            are natural candidates for your first tokens.
          </p>
        ) : (
          <div className={s.card}>
            {sources.map((src) => (
              <div key={src.filePath} className={s.cardRow}>
                <div className={s.cardRowInfo}>
                  <p className={s.cardRowLabel}>{src.filePath}</p>
                  <p className={s.cardRowDesc}>
                    {src.format}
                    {src.isPrimary ? ' · primary' : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {codebaseProfile && (
        <div className={s.section}>
          <button
            type="button"
            className={s.inlineBtn}
            onClick={redetectFromScan}
            disabled={redetecting}
            style={{ alignSelf: 'flex-start' }}
          >
            {redetecting ? (
              <>
                <CircleNotch size={12} className={s.spinIcon} />
                Re-detecting...
              </>
            ) : (
              <>
                <ArrowClockwise size={12} />
                Reset to detected values
              </>
            )}
          </button>
          <p className={s.sectionDesc}>
            Rebuilds token configuration from the most recent scan. Your manual
            edits will be replaced.
          </p>
        </div>
      )}
    </div>
  );
}

function formatTokenFormat(format: string): string {
  switch (format) {
    case 'css-custom-properties':
      return 'CSS custom properties';
    case 'scss-variables':
      return 'SCSS variables';
    case 'dtcg-json':
      return 'Design Tokens (DTCG JSON)';
    case 'tailwind-v4-theme':
      return 'Tailwind v4 @theme';
    case 'tailwind-v3-config':
      return 'Tailwind v3 config';
    case 'js-theme-object':
      return 'JavaScript theme object';
    case 'mixed':
      return 'Mixed formats';
    case 'none':
      return 'None detected';
    default:
      return format;
  }
}

function formatStylingApproach(approach: string | undefined): string {
  switch (approach) {
    case 'css-modules':
      return 'CSS Modules';
    case 'tailwind-v3':
      return 'Tailwind CSS v3';
    case 'tailwind-v4':
      return 'Tailwind CSS v4';
    case 'css-in-js':
      return 'CSS-in-JS';
    case 'scss-global':
      return 'Global SCSS';
    case 'css-global':
      return 'Global CSS';
    case 'inline':
      return 'Inline styles';
    case 'mixed':
      return 'Mixed';
    case 'unknown':
    case undefined:
      return 'Not detected';
    default:
      return approach;
  }
}

// ── Repository Panel ────────────────────────────────────────────

function SourcesPanel() {
  const { user } = useUser();
  const { orgId, org, activeRole } = useOrgContext();
  const { selectedProjectId, selectedProject } = useProjectContext();

  // Connect flow state machine
  const [phase, setPhase] = React.useState<ConnectPhase>('idle');
  const [selectedRepo, setSelectedRepo] = React.useState<string | null>(null);
  const [scanArgs, setScanArgs] = React.useState<ScanArgs | null>(null);
  const [doneResults, setDoneResults] = React.useState<ScanResult[]>([]);
  const [repoSearch, setRepoSearch] = React.useState('');

  const bindings = useQuery(
    api.projects.listBindingsByProject,
    selectedProjectId ? { projectId: selectedProjectId } : 'skip',
  );
  const activeBindingCount = useQuery(
    api.projects.countActiveBindingsByOrg,
    orgId ? { orgId } : 'skip',
  );
  // The per-org "members with GitHub connected" list is paused while we
  // migrate to per-user App user-auth (githubUserAuths). A follow-up will
  // re-introduce this view as a membership ↔ user-auth join.
  const orgPlan = (org?.plan ?? 'free') as PlanId;
  const maxRepoBindings = getQuota(orgPlan, 'maxRepoBindings');
  const repositoryQuotaLoading =
    orgId !== undefined && activeBindingCount === undefined;
  const repositoryLimitReached =
    activeBindingCount !== undefined && activeBindingCount >= maxRepoBindings;
  const canManageRepositories =
    activeRole === 'owner' || activeRole === 'admin';

  const disconnectBindingMut = useMutation(api.projects.disconnectBinding);

  const [confirmDisconnect, setConfirmDisconnect] = React.useState<
    string | null
  >(null);

  const {
    isConnected,
    isAppInstalled,
    isCheckingApp,
    appStatus,
    activeInstallation,
    appAccountLogin,
    isConnecting,
    isInstallingApp,
    error: connectError,
    connect,
    installApp,
  } = useGitHubConnect({
    orgId,
    projectId: selectedProjectId,
    source: 'sources',
  });

  const {
    repos,
    loading: reposLoading,
    error: reposError,
  } = useGitHubRepos(phase === 'picking' ? orgId : undefined);

  const {
    scanning,
    status: scanStatus,
    progress,
    results: scanResults,
    error: scanError,
    startScan,
  } = useRepoScan(phase === 'scanning' ? scanArgs : null);

  React.useEffect(() => {
    if (phase !== 'scanning') return;
    if (scanning) return;
    if (scanStatus !== 'succeeded' && !scanError) return;
    if (scanError) return; // stay in scanning phase to show error

    // Scan complete → connected. Primitive classification is no longer part
    // of the connect flow (the Contract panel is the review home now).
    setDoneResults(scanResults);
    setPhase('done');
  }, [phase, scanResults, scanError, scanStatus, scanning]);

  function handleConnectClick() {
    if (!canManageRepositories) return;
    if (repositoryQuotaLoading) return;
    if (repositoryLimitReached) return;
    if (!isConnected) {
      connect();
      return;
    }
    setPhase('picking');
    setSelectedRepo(null);
    setRepoSearch('');
  }

  const canManageGitHubApp = canManageRepositories;
  const primaryBinding = bindings?.find((binding) => binding.isPrimary);
  const repoExcluded =
    Boolean(primaryBinding) &&
    activeInstallation?.repositorySelection === 'selected' &&
    !activeInstallation.selectedRepos?.includes(primaryBinding!.repoFullName);
  const githubAccessUnhealthy = Boolean(activeInstallation?.lastVerifyError);

  function handleScan() {
    if (
      !selectedRepo ||
      !orgId ||
      !selectedProjectId ||
      repositoryQuotaLoading ||
      repositoryLimitReached
    ) {
      return;
    }
    const nextArgs = {
      orgId,
      projectId: selectedProjectId,
      repoFullName: selectedRepo,
    };
    setScanArgs(nextArgs);
    setPhase('scanning');
    void startScan(nextArgs);
  }

  function handleDone() {
    setPhase('idle');
    setScanArgs(null);
    setSelectedRepo(null);
    setDoneResults([]);
  }

  async function handleDisconnect(bindingId: string) {
    if (!user?.id) return;
    await disconnectBindingMut({
      bindingId: bindingId as Id<'projectRepoBindings'>,
      clerkUserId: user.id,
    });
    setConfirmDisconnect(null);
  }

  function handleBack() {
    setPhase('idle');
    setScanArgs(null);
    setSelectedRepo(null);
  }

  if (!selectedProjectId || !selectedProject) {
    return (
      <div className={s.detailContent}>
        <PageHeader
          title="Repository"
          titleAs="h2"
          description="Select or create a repository before configuring repository settings."
          size="md"
        />
        <ContentState
          className={s.emptyState}
          title="No repository selected."
          minHeight={160}
        />
      </div>
    );
  }

  const filteredRepos = repos.filter(
    (r) =>
      r.full_name.toLowerCase().includes(repoSearch.toLowerCase()) ||
      r.name.toLowerCase().includes(repoSearch.toLowerCase()),
  );

  return (
    <div className={s.detailContent}>
      {/* ── Repository header ── */}
      {selectedProject && (
        <PageHeader
          title={selectedProject.repoFullName ?? selectedProject.name}
          titleAs="h2"
          description="Manage this repository connection and its settings."
          size="md"
        />
      )}

      {/* ── Repository section ── */}
      <div className={s.section}>
        <p className={s.sectionTitle}>Repository</p>

        {bindings === undefined ? (
          <ContentState
            className={s.loadingState}
            icon={<CircleNotch size={14} className={s.spinIcon} />}
            title="Loading repository..."
            minHeight={160}
          />
        ) : bindings.length === 0 && phase === 'idle' ? (
          <ContentState
            className={s.emptyState}
            icon={<GitBranch size={20} className={s.emptyIcon} />}
            title="No repository connected yet."
            minHeight={160}
          />
        ) : (
          bindings.length > 0 && (
            <ul className={s.sourcesList}>
              {bindings.map((b) => {
                const isConfirming = confirmDisconnect === b._id;
                return (
                  <li key={b._id} className={s.sourceCard}>
                    <div className={s.sourceCardHeader}>
                      <GitBranch size={14} className={s.sourceIcon} />
                      <span className={s.sourceName}>{b.repoFullName}</span>
                      {b.isPrimary && !isConfirming && (
                        <span className={s.sourceRole} data-role="primary">
                          Primary
                        </span>
                      )}
                      {!isConfirming && canManageRepositories && (
                        <button
                          type="button"
                          className={s.disconnectBtn}
                          onClick={() => setConfirmDisconnect(b._id)}
                          title="Disconnect repository"
                        >
                          <Trash size={13} />
                        </button>
                      )}
                    </div>

                    {isConfirming && (
                      <div className={s.disconnectConfirm}>
                        <span className={s.disconnectConfirmText}>
                          Disconnect this repository and delete its extracted
                          data?
                        </span>
                        <div className={s.disconnectConfirmActions}>
                          <button
                            type="button"
                            className={s.disconnectConfirmBtn}
                            data-variant="danger"
                            onClick={() => handleDisconnect(b._id)}
                          >
                            Disconnect
                          </button>
                          <button
                            type="button"
                            className={s.disconnectConfirmBtn}
                            onClick={() => setConfirmDisconnect(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )
        )}

        {phase === 'idle' && (
          <button
            type="button"
            className={s.connectBtn}
            onClick={handleConnectClick}
            disabled={
              isConnecting ||
              !selectedProjectId ||
              repositoryQuotaLoading ||
              repositoryLimitReached ||
              !canManageRepositories
            }
          >
            {isConnecting ? (
              <CircleNotch size={14} className={s.spinIcon} />
            ) : (
              <Plus size={14} />
            )}
            {!canManageRepositories
              ? 'Admin required'
              : repositoryQuotaLoading
                ? 'Checking repository limit...'
                : repositoryLimitReached
                  ? 'Upgrade to add repository'
                  : 'Connect repository'}
          </button>
        )}

        {!canManageRepositories && phase === 'idle' && (
          <p className={s.errorText}>
            <Warning size={12} /> Ask an owner or admin to connect or disconnect
            repositories.
          </p>
        )}

        {repositoryLimitReached && phase === 'idle' && (
          <p className={s.errorText}>
            <Warning size={12} /> Your {orgPlan} plan includes {maxRepoBindings}{' '}
            repositor{maxRepoBindings === 1 ? 'y' : 'ies'}.
          </p>
        )}

        {connectError && (
          <p className={s.errorText}>
            <Warning size={12} /> {connectError}
          </p>
        )}

        {phase === 'picking' && (
          <div className={s.connectFlow}>
            <div className={s.connectFlowHeader}>
              <button type="button" className={s.backBtn} onClick={handleBack}>
                <ArrowLeft size={12} />
                Back
              </button>
              <p className={s.connectFlowTitle}>Select repository</p>
            </div>

            <div className={s.searchWrap}>
              <MagnifyingGlass size={13} className={s.searchIcon} />
              <input
                type="text"
                className={s.searchInput}
                placeholder="Search repositories..."
                value={repoSearch}
                onChange={(e) => setRepoSearch(e.target.value)}
              />
            </div>

            {reposLoading ? (
              <div className={s.loadingState}>
                <CircleNotch size={14} className={s.spinIcon} />
                Loading repositories...
              </div>
            ) : reposError ? (
              <p className={s.errorText}>
                <Warning size={12} /> {reposError}
              </p>
            ) : (
              <div className={s.repoList}>
                {filteredRepos.map((repo) => {
                  const alreadyConnected = bindings?.some(
                    (b) => b.repoFullName === repo.full_name,
                  );
                  return (
                    <button
                      key={repo.full_name}
                      type="button"
                      className={s.repoRow}
                      data-selected={selectedRepo === repo.full_name}
                      data-disabled={alreadyConnected || undefined}
                      disabled={alreadyConnected}
                      onClick={() => setSelectedRepo(repo.full_name)}
                    >
                      <div className={s.repoInfo}>
                        <span className={s.repoName}>
                          {repo.full_name}
                          {alreadyConnected && (
                            <span className={s.repoConnectedBadge}>
                              Already connected
                            </span>
                          )}
                        </span>
                        {!alreadyConnected && repo.private && (
                          <span className={s.repoDesc}>Private</span>
                        )}
                      </div>
                      {selectedRepo === repo.full_name && !alreadyConnected && (
                        <CheckCircle
                          size={14}
                          weight="fill"
                          className={s.repoCheck}
                        />
                      )}
                    </button>
                  );
                })}
                {filteredRepos.length === 0 && (
                  <p className={s.repoEmpty}>
                    No repositories match your search.
                  </p>
                )}
              </div>
            )}

            <button
              type="button"
              className={s.scanBtn}
              disabled={
                !selectedRepo ||
                repositoryQuotaLoading ||
                repositoryLimitReached ||
                !canManageRepositories
              }
              onClick={handleScan}
            >
              Scan repository
            </button>
            {repositoryLimitReached && (
              <p className={s.errorText}>
                <Warning size={12} /> Upgrade to add another repository.
              </p>
            )}
          </div>
        )}

        {phase === 'scanning' && (
          <div className={s.connectFlow}>
            {scanError ? (
              <div className={s.progressSection}>
                <Warning size={16} />
                <p className={s.progressText}>Scan failed</p>
                <p className={s.errorText}>
                  <Warning size={12} /> {scanError}
                </p>
                <button
                  type="button"
                  className={s.scanBtn}
                  onClick={() => {
                    setPhase('picking');
                    setSelectedRepo(null);
                  }}
                >
                  Try another repo
                </button>
              </div>
            ) : (
              <div className={s.progressSection}>
                <CircleNotch size={16} className={s.spinIcon} />
                <p className={s.progressText}>
                  Scanning {scanArgs?.repoFullName}...
                </p>
                {progress.latestComponent && (
                  <p className={s.progressSub}>
                    Found: {progress.latestComponent}
                  </p>
                )}
                <div className={s.progressBar}>
                  <div
                    className={s.progressFill}
                    style={{
                      width:
                        progress.totalFiles > 0
                          ? `${Math.round(
                              (progress.filesProcessed / progress.totalFiles) *
                                100,
                            )}%`
                          : '0%',
                    }}
                  />
                </div>
                <p className={s.progressStats}>
                  {progress.filesProcessed} / {progress.totalFiles} files
                  &nbsp;&middot;&nbsp;
                  {progress.componentsFound} components found
                  {progress.tokensFound != null &&
                    ` · ${progress.tokensFound} tokens`}
                </p>
              </div>
            )}
          </div>
        )}

        {phase === 'done' && (
          <div className={s.connectFlow}>
            <div className={s.doneSection}>
              <CheckCircle size={24} weight="fill" className={s.doneIcon} />
              <p className={s.doneTitle}>Repository connected</p>
              <p className={s.doneDesc}>
                Found {doneResults.filter((r) => r.type === 'component').length}{' '}
                components and{' '}
                {doneResults.filter((r) => r.type === 'token').length} token
                files in <strong>{scanArgs?.repoFullName}</strong>.
              </p>
              <button
                type="button"
                className={s.connectBtn}
                onClick={handleDone}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── GitHub App install prompt ── */}
      {isCheckingApp && (
        <div className={s.section}>
          <p className={s.sectionTitle}>GitHub App</p>
          <div className={s.appInstallBanner}>
            <CircleNotch size={16} className={s.spinIcon} />
            <div className={s.appInstallInfo}>
              <p className={s.appInstallTitle}>Checking...</p>
            </div>
          </div>
        </div>
      )}

      {!isCheckingApp && !isAppInstalled && (
        <div className={s.section}>
          <p className={s.sectionTitle}>GitHub App</p>
          <div className={s.appInstallBanner}>
            <div className={s.appInstallInfo}>
              <p className={s.appInstallTitle}>
                {appStatus === 'suspended'
                  ? 'Installation suspended'
                  : appStatus === 'deleted'
                    ? 'Installation deleted'
                    : isConnected
                      ? 'OAuth connected, App missing'
                      : 'Connect GitHub'}
              </p>
              <p className={s.appInstallDesc}>
                {canManageGitHubApp
                  ? isConnected
                    ? 'Install Fragments CI for branded bot comments, automated check runs, and webhook-based drift detection.'
                    : 'Connect your GitHub identity first, then install the App for this organization.'
                  : 'Ask an admin to install or reconnect Fragments CI.'}
              </p>
            </div>
            {canManageGitHubApp && (
              <button
                type="button"
                className={s.connectBtn}
                onClick={() => {
                  void (isConnected ? installApp() : connect());
                }}
                disabled={isConnecting || isInstallingApp || !selectedProjectId}
              >
                {isConnecting || isInstallingApp ? (
                  <CircleNotch size={14} className={s.spinIcon} />
                ) : (
                  <Plus size={14} />
                )}
                {isConnected ? 'Install app' : 'Connect GitHub'}
              </button>
            )}
          </div>
        </div>
      )}

      {!isCheckingApp && isAppInstalled && (
        <div className={s.section}>
          <p className={s.sectionTitle}>GitHub App</p>
          <div className={s.appInstallBanner}>
            <CheckCircle
              size={16}
              weight="fill"
              className={s.appInstalledIcon}
            />
            <div className={s.appInstallInfo}>
              <p className={s.appInstalledText}>
                {repoExcluded
                  ? 'App connected, repo excluded'
                  : githubAccessUnhealthy
                    ? 'GitHub access unhealthy'
                    : appAccountLogin
                      ? `Connected to @${appAccountLogin}`
                      : 'Connected to GitHub'}
              </p>
              <p className={s.appInstallDesc}>
                {repoExcluded
                  ? 'Grant the Fragments GitHub App access to this repository in GitHub.'
                  : githubAccessUnhealthy
                    ? 'Reconnect your GitHub identity, or ask an admin to reinstall Fragments CI.'
                    : 'Fragments CI can access this repository using the organization App installation.'}
              </p>
            </div>
            {canManageGitHubApp ? (
              <button
                type="button"
                className={s.reconnectBtn}
                onClick={() => {
                  void installApp();
                }}
                disabled={isInstallingApp}
              >
                {isInstallingApp ? (
                  <CircleNotch size={12} className={s.spinIcon} />
                ) : null}
                {isInstallingApp ? 'Opening…' : 'Reconnect'}
              </button>
            ) : null}
          </div>
          {/*
            Per-org "members with GitHub connected" was sourced from the
            removed `githubOAuthConnections` table. A follow-up will rebuild
            it from the per-user `githubUserAuths` joined with org
            memberships.
          */}
        </div>
      )}
    </div>
  );
}

// ── Danger Panel (repository-level) ─────────────────────────────

function DangerPanel() {
  const { user } = useUser();
  const navigate = useNavigate();
  const { activeRole } = useOrgContext();
  const { selectedProjectId, selectedProject, projects, setProject } =
    useProjectContext();
  const deleteProjectMut = useAction(api.projects.deleteProject);
  const [confirmDeleteProject, setConfirmDeleteProject] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const canDeleteProject = activeRole === 'owner' || activeRole === 'admin';

  if (!selectedProjectId || !selectedProject) {
    return (
      <div className={s.detailContent}>
        <PageHeader
          title="Danger zone"
          titleAs="h2"
          description="Select a repository to manage destructive actions."
          size="md"
        />
        <ContentState
          className={s.emptyState}
          title="No repository selected."
          minHeight={160}
        />
      </div>
    );
  }
  const repositoryName = selectedProject.repoFullName ?? selectedProject.name;

  async function handleDeleteProject() {
    if (!canDeleteProject) return;
    if (!selectedProjectId || !user?.id) return;
    setIsDeleting(true);
    try {
      await deleteProjectMut({
        projectId: selectedProjectId,
        clerkUserId: user.id,
      });
      setConfirmDeleteProject(false);
      const remaining = projects.filter((p) => p._id !== selectedProjectId);
      if (remaining.length > 0) {
        setProject(remaining[0].slug);
      } else {
        navigate({ to: '/onboarding' as any });
      }
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className={s.detailContent}>
      <PageHeader
        title="Danger zone"
        titleAs="h2"
        description={`Irreversible actions for ${repositoryName}.`}
        size="md"
      />

      <div className={s.section} data-danger>
        <p className={s.sectionTitle}>Danger zone</p>
        {!canDeleteProject && (
          <p className={s.sectionDesc}>
            Ask an owner or admin to delete repositories.
          </p>
        )}
        {!confirmDeleteProject ? (
          <div className={s.dangerRow}>
            <div className={s.dangerInfo}>
              <p className={s.dangerLabel}>Delete repository</p>
              <p className={s.dangerDesc}>
                Permanently delete <strong>{repositoryName}</strong> and all
                associated data including components, tokens, findings,
                suggestions, and repository connections.
              </p>
            </div>
            <button
              type="button"
              className={s.dangerBtn}
              onClick={() => setConfirmDeleteProject(true)}
              disabled={!canDeleteProject}
            >
              {canDeleteProject ? 'Delete repository' : 'Admin required'}
            </button>
          </div>
        ) : (
          <div className={s.dangerConfirm}>
            <Warning size={16} className={s.dangerConfirmIcon} />
            <p className={s.dangerConfirmText}>
              Are you sure? This will permanently delete{' '}
              <strong>{repositoryName}</strong> and all its data. This action
              cannot be undone.
            </p>
            <div className={s.dangerConfirmActions}>
              <button
                type="button"
                className={s.disconnectConfirmBtn}
                data-variant="danger"
                onClick={handleDeleteProject}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Yes, delete repository'}
              </button>
              <button
                type="button"
                className={s.disconnectConfirmBtn}
                onClick={() => setConfirmDeleteProject(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
